import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { Loader2, Swords, Trophy, Clock, LogIn } from "lucide-react";
import { toast } from "sonner";
import { RouteTopBar } from "@/components/layout/RouteTopBar";
import {
  getFriendChallengePublic,
  joinFriendChallenge,
  finalizeFriendChallenge,
  type ChallengePublic,
  type ChallengeScore,
} from "@/lib/friendChallenges.functions";
import { supabase } from "@/integrations/supabase/client";
import { trackEvent } from "@/lib/audit";

export const Route = createFileRoute("/challenge/$code")({
  head: ({ params }) => ({
    meta: [
      { title: `Reto ${params.code} · VerseSport` },
      {
        name: "description",
        content: `Acepta el reto ${params.code}. Predice mejor que tu amigo durante el Mundial 2026.`,
      },
      { property: "og:title", content: `🔥 Te reto en VerseSport — ${params.code}` },
      {
        property: "og:description",
        content: "Predice mejor que yo durante el Mundial. Únete al reto en VerseSport.",
      },
    ],
  }),
  component: ChallengePage,
});

function fmtRemaining(iso: string): string {
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return "Reto finalizado";
  const h = Math.floor(ms / 3_600_000);
  const m = Math.floor((ms % 3_600_000) / 60_000);
  const s = Math.floor((ms % 60_000) / 1000);
  return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
}

function ScoreCard({
  score,
  position,
  isMe,
  isWinner,
  empty,
}: {
  score?: ChallengeScore;
  position: 1 | 2;
  isMe: boolean;
  isWinner: boolean;
  empty?: boolean;
}) {
  return (
    <div
      className={`flex-1 rounded-2xl border p-3 text-center transition ${
        isWinner
          ? "border-[oklch(0.82_0.24_145)]/60 bg-[oklch(0.82_0.24_145)]/10"
          : "border-border bg-background"
      }`}
    >
      <div className="text-[9px] font-bold tracking-widest text-muted-foreground">
        JUGADOR {position}
      </div>
      <div className="mt-1 truncate text-sm font-black">
        {empty ? "Esperando rival…" : score?.display_name ?? "Fan"}
        {isMe && <span className="ml-1 text-[10px] font-bold text-primary">(tú)</span>}
      </div>
      <div className="mt-2 text-3xl font-black tabular-nums">{score?.wins ?? 0}</div>
      <div className="text-[10px] text-muted-foreground">aciertos</div>
      <div className="mt-1 text-[11px] font-bold text-foreground tabular-nums">
        {score?.xp ?? 0} XP
      </div>
      {isWinner && (
        <div className="mt-2 inline-flex items-center gap-1 rounded-full bg-[oklch(0.82_0.24_145)]/25 px-2 py-0.5 text-[10px] font-black text-[oklch(0.55_0.18_145)]">
          <Trophy className="h-3 w-3" /> CHAMPION
        </div>
      )}
    </div>
  );
}

function ChallengePage() {
  const { code } = Route.useParams();
  const navigate = useNavigate();
  const fetchPublic = useServerFn(getFriendChallengePublic);
  const join = useServerFn(joinFriendChallenge);
  const finalize = useServerFn(finalizeFriendChallenge);

  const [me, setMe] = useState<string | null>(null);
  const [data, setData] = useState<ChallengePublic | null>(null);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [tick, setTick] = useState(0);

  const load = async () => {
    try {
      const res = await fetchPublic({ data: { code } });
      setData(res);
      if (res && res.status !== "finished" && new Date(res.expires_at).getTime() < Date.now()) {
        try {
          await finalize({ data: { challengeId: res.id } });
          const after = await fetchPublic({ data: { code } });
          setData(after);
          if (after?.winner_user_id) {
            void trackEvent("challenge_completed", { code, winner: after.winner_user_id });
          }
        } catch {
          /* noop */
        }
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let alive = true;
    void supabase.auth.getUser().then(({ data: u }) => {
      if (alive) setMe(u.user?.id ?? null);
    });
    void load();
    const id = window.setInterval(() => setTick((n) => n + 1), 1000);
    const poll = window.setInterval(() => void load(), 15_000);
    return () => {
      alive = false;
      window.clearInterval(id);
      window.clearInterval(poll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  const creatorScore = useMemo(
    () => data?.scores.find((s) => s.user_id === data.creator_user_id),
    [data],
  );
  const opponentScore = useMemo(
    () => data?.scores.find((s) => s.user_id === data.opponent_user_id),
    [data],
  );

  const handleJoin = async () => {
    if (!me) {
      try {
        sessionStorage.setItem("ss2026_pending_challenge", code);
      } catch {
        /* noop */
      }
      void navigate({ to: "/registro" });
      return;
    }
    setJoining(true);
    try {
      await join({ data: { code } });
      void trackEvent("challenge_joined", { code });
      toast.success("Reto aceptado, ¡a predecir!");
      await load();
    } catch (e) {
      const msg = e instanceof Error ? e.message : "No se pudo unir";
      toast.error(msg);
    } finally {
      setJoining(false);
    }
  };

  if (loading) {
    return (
      <main className="grid min-h-screen place-items-center overflow-x-hidden bg-background">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </main>
    );
  }

  if (!data) {
    return (
      <main className="min-h-screen overflow-x-hidden bg-background">
        <RouteTopBar fallback="/retos" />
        <div className="mx-auto max-w-md p-6 text-center">
          <h1 className="text-lg font-black">Reto no encontrado</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            El código <code className="font-mono">{code}</code> no existe o expiró.
          </p>
        </div>
      </main>
    );
  }

  const isCreator = me === data.creator_user_id;
  const isOpponent = me === data.opponent_user_id;
  const isParticipant = isCreator || isOpponent;
  const canJoin = data.status !== "finished" && !data.opponent_user_id && !isCreator;
  const finished = data.status === "finished";
  const winnerId = data.winner_user_id;

  const leadingId = (() => {
    if (winnerId) return winnerId;
    const a = creatorScore?.wins ?? 0;
    const b = opponentScore?.wins ?? 0;
    if (a > b) return data.creator_user_id;
    if (b > a) return data.opponent_user_id;
    return null;
  })();

  // tick is read so the countdown re-renders every second
  void tick;

  return (
    <main className="min-h-screen overflow-x-hidden bg-background">
      <RouteTopBar fallback="/retos" />
      <div className="mx-auto w-full max-w-md space-y-4 px-3 py-5 sm:px-6">
        <header className="rounded-2xl border border-border bg-gradient-to-br from-[oklch(0.58_0.24_27)]/15 via-background to-background p-4 text-center">
          <div className="flex items-center justify-center gap-2 text-[10px] font-bold tracking-widest text-muted-foreground">
            <Swords className="h-3.5 w-3.5 text-[oklch(0.65_0.22_30)]" /> RETO VS AMIGO
          </div>
          <div className="mt-1 text-2xl font-black tracking-widest">{data.code}</div>
          <div className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-background/60 px-3 py-1 text-[11px] font-bold tabular-nums">
            <Clock className="h-3 w-3" />
            {finished ? "Reto finalizado" : fmtRemaining(data.expires_at)}
          </div>
        </header>

        <div className="flex items-stretch gap-2">
          <ScoreCard
            score={creatorScore}
            position={1}
            isMe={isCreator}
            isWinner={finished && winnerId === data.creator_user_id}
          />
          <div className="flex items-center text-xs font-black text-muted-foreground">VS</div>
          <ScoreCard
            score={opponentScore}
            position={2}
            isMe={isOpponent}
            isWinner={finished && winnerId === data.opponent_user_id}
            empty={!data.opponent_user_id}
          />
        </div>

        {!finished && leadingId && (
          <div className="rounded-xl border border-border bg-background/60 px-3 py-2 text-center text-[11px]">
            <span className="font-bold text-muted-foreground">Liderando: </span>
            <span className="font-black">
              {leadingId === data.creator_user_id
                ? creatorScore?.display_name
                : opponentScore?.display_name}
            </span>
          </div>
        )}

        {canJoin && (
          <button
            type="button"
            onClick={handleJoin}
            disabled={joining}
            className="w-full rounded-xl bg-gradient-to-r from-[oklch(0.58_0.24_27)] to-[oklch(0.65_0.22_30)] px-4 py-3 text-sm font-black text-white shadow-lg shadow-red-900/30 transition active:scale-95 disabled:opacity-60"
          >
            {joining ? (
              <span className="inline-flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" /> Aceptando reto…
              </span>
            ) : me ? (
              <>⚔️ Aceptar el reto</>
            ) : (
              <span className="inline-flex items-center gap-2">
                <LogIn className="h-4 w-4" /> Regístrate y acepta el reto
              </span>
            )}
          </button>
        )}

        {finished && (
          <div className="rounded-2xl border border-[oklch(0.82_0.24_145)]/50 bg-[oklch(0.82_0.24_145)]/10 p-4 text-center">
            <Trophy className="mx-auto h-6 w-6 text-[oklch(0.55_0.18_145)]" />
            <div className="mt-2 text-sm font-black">
              {winnerId
                ? `Ganador: ${winnerId === data.creator_user_id ? creatorScore?.display_name : opponentScore?.display_name}`
                : "Empate"}
            </div>
            {winnerId && (
              <div className="mt-1 text-[11px] text-muted-foreground">
                +100 XP · Badge <strong className="text-foreground">FRIEND CHAMPION</strong>
              </div>
            )}
          </div>
        )}

        {isParticipant && !finished && (
          <p className="text-center text-[11px] text-muted-foreground">
            Cada predicción correcta suma una victoria a tu marcador del reto.
          </p>
        )}
      </div>
    </main>
  );
}